import { useMunicipio } from "@/contexts/cadastro/municipio";
import { useFormularioRespostaControle } from "@/contexts/fiscalizacao/formulario";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  Form,
  List,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useState } from "react";

interface FormValues {
  municipio: { id: number };
}

export function DadosSyncMunicipio() {
  const { isOnline } = useNetworkStatus();
  const [form] = Form.useForm<FormValues>();
  const [isSyncing, setIsSyncing] = useState(false);
  const { getMunicipiosMt } = useMunicipio();
  const {
    getFormulariosRespostaControle,
    sincronizarFormulariosControleRespostas,
    removerMunicipio,
    countMunicipiosSynced,
    municipiosSincronizados,
  } = useFormularioRespostaControle();
  const { message, notification } = App.useApp();

  const { data: municipios = [] } = useQuery({
    queryKey: ["municipios-mt"],
    queryFn: getMunicipiosMt,
    enabled: isOnline,
  });

  const handleSubmit = async (values: FormValues) => {
    if (countMunicipiosSynced === 3) {
      notification.info({
        message: "Limite de sincronização atingido",
        description: `Você já sincronizou os dados dos 3 municípios permitidos.`,
      });
      return;
    }

    try {
      setIsSyncing(true);
      message.loading({
        content: "Sincronizando dados do município...",
        key: "sync",
      });

      const formularios = await getFormulariosRespostaControle(
        values.municipio.id
      );

      sincronizarFormulariosControleRespostas(formularios);

      message.destroy("sync");
      setIsSyncing(false);
      form.resetFields();

      notification.success({
        message: "Sincronização concluída",
        description: `Foram sincronizados ${formularios.length} formulários de resposta de controle.`,
      });
    } catch (error) {
      console.log("Erro ao sincronizar dados do município:", error);
      setIsSyncing(false);
      message.destroy("sync");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possivel sincronizar dados do município.",
        description: messageError,
      });
    }
  };

  const handleRemoveMunicipio = (municipioId: number) => {
    removerMunicipio(municipioId);
    notification.success({
      message: "Município removido",
      description: "Os dados do município foram removidos com sucesso.",
    });
  };

  // Filtrar municípios já sincronizados do select
  const municipiosDisponiveisParaSincronizacao = municipios.filter(
    (municipio) =>
      !municipiosSincronizados.some((sync) => sync.id === municipio.id)
  );

  return (
    <>
      <Divider>
        <Typography.Title level={5}>
          Sincronizar Formularios de Controle de Resposta do Relatório de
          Auditoria e Monitoria para uso offline
        </Typography.Title>
      </Divider>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[16, 16]} justify="center">
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Município"
              name={["municipio", "id"]}
              rules={[{ required: true, message: "Selecione um município" }]}
            >
              <Select
                disabled={!isOnline || isSyncing || countMunicipiosSynced === 3}
                placeholder="Selecione o município"
                allowClear
                showSearch
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                optionFilterProp="label"
                options={municipiosDisponiveisParaSincronizacao.map(
                  (municipio) => ({
                    label: `${municipio.nome} - ${municipio.uf.sigla}`,
                    value: municipio.id,
                  })
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="center" gutter={[16, 16]}>
          <Col>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<DownloadOutlined />}
                loading={isSyncing}
                disabled={isSyncing || !isOnline || countMunicipiosSynced === 3}
              >
                Sincronizar
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider>
        <Typography.Title level={5}>
          Municípios Sincronizados ({countMunicipiosSynced}/3)
        </Typography.Title>
      </Divider>

      <Row justify="center">
        <Col xs={24}>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 2,
              xl: 3,
              xxl: 3,
            }}
            dataSource={municipiosSincronizados}
            renderItem={(municipio) => (
              <List.Item key={municipio.id}>
                <Card
                  size="small"
                  styles={{
                    header: {
                      backgroundColor: "#fafafa",
                      borderRadius: 0,
                    },
                    body: {
                      backgroundColor: "#f0f2f5",
                      borderRadius: 0,
                    },
                    actions: {
                      backgroundColor: "#fafafa",
                      borderRadius: 0,
                    },
                  }}
                  style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
                  title={
                    <Space>
                      <Typography.Text strong>{municipio.nome}</Typography.Text>
                      <Tag color="blue" icon={<FileTextOutlined />}>
                        {municipio.quantidadeFormularios} Respostas encontradas
                      </Tag>
                    </Space>
                  }
                  extra={[
                    <Popconfirm
                      title="Remover"
                      description="Tem certeza que deseja remover este município?"
                      onConfirm={() => handleRemoveMunicipio(municipio.id)}
                      okText="Sim"
                      cancelText="Não"
                    >
                      <Tooltip title="Remover">
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                />
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </>
  );
}
